import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/app/types/store';
import { selectMenuItems } from '@/entities/layout';

import { toNavigationItem } from '../lib/to-navigation-item';
import { STATIC_MENU_ITEMS } from './constants';
import type { NavigationItem, NavigationItemWithState } from './types';

/**
 * Creates a selector for navigation items with projectId and siteId parameters
 * Combines static frontend menu items with dynamic backend items
 */
export const createNavigationItemsSelector =
  (projectId?: number, siteId?: number) =>
  (state: RootState): NavigationItem[] => {
    const backendItems = projectId && siteId ? selectMenuItems(projectId, siteId)(state) || [] : [];

    return [...STATIC_MENU_ITEMS, ...backendItems.map(toNavigationItem)];
  };

// Default selector for cases where we don't have projectId/siteId yet
export const selectNavigationItems = createNavigationItemsSelector();
export const selectNavigationState = (state: RootState) => state.navigation;
export const selectExpandedItems = createSelector([selectNavigationState], (navigation) => navigation.expandedItems);
export const selectIsCollapsed = createSelector([selectNavigationState], (navigation) => navigation.isCollapsed);
export const selectIsMobileOpen = createSelector([selectNavigationState], (navigation) => navigation.isMobileOpen);

export const createNavigationItemsWithStateSelector =
  (projectId?: number, siteId?: number) =>
  (state: RootState, currentPath: string): NavigationItemWithState[] => {
    const items = createNavigationItemsSelector(projectId, siteId)(state);
    const navigationState = selectNavigationState(state);

    const enrichItem = (item: NavigationItem): NavigationItemWithState => {
      const subItems = item.subItems?.map(enrichItem);
      const isActive = item.path === currentPath || subItems?.some((sub) => sub.isActive) || false;

      return {
        ...item,
        isActive,
        isExpanded: !!navigationState.expandedItems[item.code],
        subItems
      };
    };

    return items.map(enrichItem);
  };
